/**
 * TwoFactorSetup - Main component for 2FA TOTP enrollment flow
 *
 * Steps:
 * 1. Initial - Show enable/disable 2FA button
 * 2. QR Display - Show QR code and manual entry key
 * 3. Verification - Enter 6-digit code from authenticator
 * 4. Backup Codes - Display and save backup codes
 * 5. Complete - Show success state with option to disable
 */
import { useEffect, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
  InputOTPSeparator,
} from '@/components/ui/input-otp';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Shield,
  ShieldCheck,
  ShieldOff,
  Copy,
  Check,
  Loader2,
  ChevronRight,
  AlertTriangle,
  Smartphone,
} from 'lucide-react';
import { toast } from 'sonner';
import { useTwoFactorSetup, TwoFactorStep } from '@/hooks/useTwoFactorSetup';
import { BackupCodesDisplay } from './BackupCodesDisplay';

export function TwoFactorSetup() {
  const {
    currentStep,
    loading,
    error,
    status,
    enrollment,
    backupCodes,
    startEnrollment,
    verifyCode,
    disable2FA,
    checkStatus,
    reset,
    setStep,
  } = useTwoFactorSetup();

  const [otpValue, setOtpValue] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [secretCopied, setSecretCopied] = useState(false);
  const [showDisableDialog, setShowDisableDialog] = useState(false);
  const [disabling, setDisabling] = useState(false);

  // Check 2FA status on mount
  useEffect(() => {
    checkStatus();
  }, [checkStatus]);

  const handleStartSetup = async () => {
    await startEnrollment();
  };

  const handleVerifyCode = async () => {
    if (otpValue.length !== 6) {
      toast.error('Please enter a 6-digit code');
      return;
    }

    setVerifying(true);
    const success = await verifyCode(otpValue);
    setVerifying(false);

    if (success) {
      toast.success('Two-factor authentication enabled!');
    }
  };

  const handleCopySecret = async () => {
    if (enrollment?.secret) {
      try {
        await navigator.clipboard.writeText(enrollment.secret);
        setSecretCopied(true);
        toast.success('Secret key copied to clipboard');
        setTimeout(() => setSecretCopied(false), 3000);
      } catch (err) {
        toast.error('Failed to copy secret key');
      }
    }
  };

  const handleDisable2FA = async () => {
    setDisabling(true);
    const success = await disable2FA();
    setDisabling(false);
    setShowDisableDialog(false);

    if (success) {
      toast.success('Two-factor authentication disabled');
    } else {
      toast.error('Failed to disable 2FA');
    }
  };

  const handleBackupCodesComplete = () => {
    setStep('complete');
    toast.success('2FA setup complete! Your account is now more secure.');
  };

  // Loading state
  if (loading && currentStep === 'initial' && !status) {
    return (
      <Card>
        <CardHeader className="p-4 sm:p-6">
          <Skeleton className="h-5 w-48" />
          <Skeleton className="h-4 w-64 mt-2" />
        </CardHeader>
        <CardContent className="p-4 pt-0 sm:p-6 sm:pt-0">
          <Skeleton className="h-10 w-full" />
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader className="p-4 sm:p-6">
          <div className="flex items-start gap-3">
            <div className={`p-2 rounded-lg ${
              status?.isVerified
                ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                : 'bg-muted'
            }`}>
              {status?.isVerified ? (
                <ShieldCheck className="h-5 w-5" />
              ) : (
                <Shield className="h-5 w-5" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <CardTitle className="text-base sm:text-lg flex items-center gap-2 flex-wrap">
                Two-Factor Authentication
                {status?.isVerified && (
                  <Badge variant="default" className="bg-green-600">
                    Enabled
                  </Badge>
                )}
              </CardTitle>
              <CardDescription className="mt-1">
                {status?.isVerified
                  ? 'Your account is protected with 2FA'
                  : 'Add an extra layer of security to your account'}
              </CardDescription>
            </div>
          </div>
        </CardHeader>

        <CardContent className="p-4 pt-0 sm:p-6 sm:pt-0 space-y-4">
          {/* Error Alert */}
          {error && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Step: Initial / Complete */}
          {(currentStep === 'initial' || currentStep === 'complete') && (
            <>
              {status?.isVerified ? (
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-3 border rounded-lg">
                    <div className="flex items-center gap-3">
                      <Smartphone className="h-5 w-5 text-muted-foreground" />
                      <div>
                        <p className="text-sm font-medium">Authenticator App</p>
                        <p className="text-xs text-muted-foreground">
                          {status.factorName || 'TOTP Authenticator'}
                        </p>
                      </div>
                    </div>
                    <ShieldCheck className="h-5 w-5 text-green-600" />
                  </div>

                  <Button
                    variant="outline"
                    className="w-full text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950"
                    onClick={() => setShowDisableDialog(true)}
                  >
                    <ShieldOff className="h-4 w-4 mr-2" />
                    Disable Two-Factor Authentication
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="p-4 bg-muted rounded-lg">
                    <h4 className="font-medium text-sm mb-2">Why enable 2FA?</h4>
                    <ul className="text-sm text-muted-foreground space-y-1">
                      <li>• Protects your account even if your password is compromised</li>
                      <li>• Uses time-based codes from an authenticator app</li>
                      <li>• Backup codes ensure you never lose access</li>
                    </ul>
                  </div>

                  <Button
                    className="w-full gap-2"
                    onClick={handleStartSetup}
                    disabled={loading}
                  >
                    {loading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Shield className="h-4 w-4" />
                    )}
                    Enable Two-Factor Authentication
                    <ChevronRight className="h-4 w-4 ml-auto" />
                  </Button>
                </div>
              )}
            </>
          )}

          {/* Step: QR Display */}
          {currentStep === 'qr_display' && enrollment && (
            <div className="space-y-4">
              <Alert>
                <Smartphone className="h-4 w-4" />
                <AlertDescription>
                  Scan the QR code below with your authenticator app (Google Authenticator, Authy, etc.)
                </AlertDescription>
              </Alert>

              {/* QR Code */}
              <div className="flex justify-center p-6 bg-white rounded-lg border">
                <QRCodeSVG
                  value={enrollment.totpUri}
                  size={200}
                  level="M"
                  includeMargin={true}
                />
              </div>

              {/* Manual Entry */}
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground text-center">
                  Can't scan? Enter this key manually:
                </p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 p-3 bg-muted rounded-lg font-mono text-sm break-all text-center">
                    {enrollment.secret}
                  </code>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={handleCopySecret}
                    className="shrink-0"
                  >
                    {secretCopied ? (
                      <Check className="h-4 w-4 text-green-600" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>

              <Button
                className="w-full"
                onClick={() => setStep('verification')}
              >
                Continue to Verification
                <ChevronRight className="h-4 w-4 ml-2" />
              </Button>

              <Button
                variant="ghost"
                className="w-full"
                onClick={reset}
              >
                Cancel Setup
              </Button>
            </div>
          )}

          {/* Step: Verification */}
          {currentStep === 'verification' && (
            <div className="space-y-4">
              <Alert>
                <Shield className="h-4 w-4" />
                <AlertDescription>
                  Enter the 6-digit code from your authenticator app to verify setup
                </AlertDescription>
              </Alert>

              {/* OTP Input */}
              <div className="flex flex-col items-center gap-4 py-4">
                <InputOTP
                  maxLength={6}
                  value={otpValue}
                  onChange={setOtpValue}
                  disabled={verifying}
                >
                  <InputOTPGroup>
                    <InputOTPSlot index={0} />
                    <InputOTPSlot index={1} />
                    <InputOTPSlot index={2} />
                  </InputOTPGroup>
                  <InputOTPSeparator />
                  <InputOTPGroup>
                    <InputOTPSlot index={3} />
                    <InputOTPSlot index={4} />
                    <InputOTPSlot index={5} />
                  </InputOTPGroup>
                </InputOTP>

                <p className="text-sm text-muted-foreground">
                  Enter the code displayed in your authenticator app
                </p>
              </div>

              <Button
                className="w-full"
                onClick={handleVerifyCode}
                disabled={otpValue.length !== 6 || verifying}
              >
                {verifying ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Verifying...
                  </>
                ) : (
                  <>
                    Verify and Enable 2FA
                    <ChevronRight className="h-4 w-4 ml-2" />
                  </>
                )}
              </Button>

              <Button
                variant="ghost"
                className="w-full"
                onClick={() => setStep('qr_display')}
                disabled={verifying}
              >
                Back to QR Code
              </Button>
            </div>
          )}

          {/* Step: Backup Codes */}
          {currentStep === 'backup_codes' && backupCodes.length > 0 && (
            <BackupCodesDisplay
              codes={backupCodes}
              onComplete={handleBackupCodesComplete}
            />
          )}
        </CardContent>
      </Card>

      {/* Disable 2FA Confirmation Dialog */}
      <Dialog open={showDisableDialog} onOpenChange={setShowDisableDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Disable Two-Factor Authentication?</DialogTitle>
            <DialogDescription>
              This will remove the extra layer of security from your account.
              You can enable it again at any time.
            </DialogDescription>
          </DialogHeader>

          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              Your account will only be protected by your password after disabling 2FA.
            </AlertDescription>
          </Alert>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setShowDisableDialog(false)}
              disabled={disabling}
            >
              Keep 2FA Enabled
            </Button>
            <Button
              variant="destructive"
              onClick={handleDisable2FA}
              disabled={disabling}
            >
              {disabling ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Disabling...
                </>
              ) : (
                'Disable 2FA'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
